import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetCatalogQuery, useMeQuery } from '@platform/state/api';
import { Icon } from '@platform/ui';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { SearchGroupView } from './SearchGroupView';
import { useRecordSearch } from './useRecordSearch';
import './search.css';

/**
 * Пошук записів робочого простору (patterns/search, прототип v4 G:L2597–2632): «Пошук», «Кожен запис у просторі — за ідентифікатором,
 * назвою чи текстом.», пошук-пігулка 46px з лічильником «N результатів»; шукає під час набору (від двох символів, «Введіть щонайменше два
 * символи.»), за кодом запису (TSK-3) і текстом; групи за типами записів з кількістю, статуси мовою інтерфейсу; «Жоден запис не відповідає «q».».
 * Запит — у адресі (?q=), тож пошук верхньої панелі й «Назад» браузера його зберігають. Як і в прототипі (autoFocus, outline:none), поле
 * отримує фокус, щойно сторінка відкривається (набір одразу шукає), але рамки фокусу не показує.
 */
export function SearchPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const { ws } = useWorkspaceParams();
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const { data: me } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const { query, result } = useRecordSearch(ws, text);
  useEffect(() => { setText(params.get('q') ?? ''); }, [params]);
  const onChange = (value: string) => { setText(value); setParams(value.trim() ? { q: value } : {}, { replace: true }); };
  const nameOf = (module: string, rt?: string | null) => {
    const summary = catalog?.modules.find((m) => m.key === module);
    return label(rt ? summary?.recordTypes.find((r) => r.key === rt)?.name : summary?.name) || rt || module;
  };
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  return (
    <div className="sr-page">
      <div className="sr-head">
        <h1>{t('searchPage.title')}</h1>
        <div className="muted">{t('searchPage.sub', { ws: wsName })}</div>
      </div>
      <label className="sr-box">
        <Icon name="search" size={16} strokeWidth={2} />
        <input autoFocus value={text} placeholder={t('searchPage.placeholder')} aria-label={t('searchPage.label')} onChange={(e) => onChange(e.target.value)} />
        <span className="sr-box__count">{result ? t('searchPage.results', { count: result.total }) : ''}</span>
      </label>
      {text.trim().length < 2 && <div className="muted">{t('searchPage.hint')}</div>}
      {result?.groups.map((g) => <SearchGroupView key={g.key || g.module} group={g} name={g.name || nameOf(g.module, g.recordType)} />)}
      {result && !result.groups.length && <div className="sr-empty">{t('searchPage.empty', { q: query })}</div>}
    </div>
  );
}
